import React, { useEffect, useState, useRef } from "react";
import {
  db,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "../firebase";
import { v4 as uuidv4 } from "uuid";
import LoadingSpinner from "../components/LoadingSpinner";
import CollapsibleSection from "../components/CollapsibleSection";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>
          {message}
        </p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ trailer, onClose, onEdit, onAssign, onDelete, hasActiveTrips }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for Trailer: {trailer.registration}</h3>
        {hasActiveTrips ? (
          <p style={{ color: "#b91c1c" }}>
            This trailer is assigned to a truck on an active trip. You can only edit or delete it.
          </p>
        ) : (
          <p>Select an action to perform on this trailer.</p>
        )}
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onEdit}>
            Edit
          </button>
          <button
            className="btn-secondary"
            onClick={onAssign}
            disabled={hasActiveTrips}
            style={{ opacity: hasActiveTrips ? 0.5 : 1 }}
          >
            Assign
          </button>
          <button
            className="btn-delete"
            onClick={onDelete}
            disabled={hasActiveTrips}
            style={{ opacity: hasActiveTrips ? 0.5 : 1 }}
          >
            Delete
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AssignModal = ({ trailer, onClose, onAssign, trucks, trips }) => {
  const [assignForm, setAssignForm] = useState({
    assigned_truck: trailer.assigned_truck || "",
  });
  const [feedback, setFeedback] = useState(null);
  const [reassignConfirm, setReassignConfirm] = useState(null);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setFeedback(null);
        setReassignConfirm(null);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasActiveTrips = (truckReg, tripsList) => {
    return tripsList.some((t) => t.truck === truckReg && t.status === "Active");
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setFeedback(null);
    try {
      const selectedTruck = trucks.find((t) => t.reg_no === assignForm.assigned_truck);
      if (selectedTruck && hasActiveTrips(selectedTruck.reg_no, trips)) {
        setFeedback({
          message: "Cannot assign to this truck because it is on an active trip.",
          type: "error",
        });
        return;
      }
      if (selectedTruck && selectedTruck.trailers?.length > 0) {
        setReassignConfirm({
          message: `The truck ${selectedTruck.reg_no} is already assigned to trailer(s): ${selectedTruck.trailers.join(
            ", "
          )}. Do you want to reassign it to this trailer? This will unassign the other trailers.`,
        });
        return;
      }
      await performAssignment();
    } catch (error) {
      console.error("Error assigning trailer:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to assign trailer: ${error.message}`,
        type: "error",
      });
    }
  };

  const performAssignment = async () => {
    try {
      // Update trailer
      await updateDoc(doc(db, "trailers", trailer.id), {
        assigned_truck: assignForm.assigned_truck,
      });
      // Update truck trailers
      const selectedTruck = trucks.find((t) => t.reg_no === assignForm.assigned_truck);
      if (selectedTruck) {
        const existingTrailers = selectedTruck.trailers || [];
        const newTrailers = existingTrailers.includes(trailer.registration)
          ? existingTrailers
          : [...existingTrailers, trailer.registration];
        await updateDoc(doc(db, "trucks", selectedTruck.id), {
          trailers: newTrailers,
        });
        // Unassign other trailers from this truck
        const otherTrailers = trailers.filter(
          (t) =>
            t.assigned_truck === selectedTruck.reg_no &&
            t.registration !== trailer.registration
        );
        await Promise.all(
          otherTrailers.map((t) =>
            updateDoc(doc(db, "trailers", t.id), { assigned_truck: "" })
          )
        );
      }
      // Unassign trailer from previous truck
      const previousTruck = trucks.find((t) => t.trailers?.includes(trailer.registration));
      if (previousTruck && previousTruck.reg_no !== assignForm.assigned_truck) {
        await updateDoc(doc(db, "trucks", previousTruck.id), {
          trailers: previousTruck.trailers.filter((t) => t !== trailer.registration),
        });
      }
      setFeedback({ message: "Trailer assigned successfully.", type: "success" });
      onAssign();
      onClose();
    } catch (error) {
      console.error("Error performing assignment:", error);
      setFeedback({
        message: `Failed to assign trailer: ${error.message}`,
        type: "error",
      });
    }
  };

  const renderTruckDropdown = () => {
    const isPlaceholder = !assignForm.assigned_truck;
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${
            openDropdown === "assigned_truck" ? "active" : ""
          } ${isPlaceholder ? "placeholder" : ""}`}
          onClick={() =>
            setOpenDropdown(
              openDropdown === "assigned_truck" ? null : "assigned_truck"
            )
          }
        >
          {isPlaceholder ? "Select Truck" : assignForm.assigned_truck}
        </div>
        {openDropdown === "assigned_truck" && (
          <div className="dropdown">
            <div
              className={`dropdown-option ${
                !assignForm.assigned_truck ? "selected" : ""
              }`}
              onClick={() => {
                setAssignForm({ ...assignForm, assigned_truck: "" });
                setOpenDropdown(null);
              }}
            >
              None
            </div>
            {trucks.length > 0 ? (
              trucks.map((truck) => (
                <div
                  key={truck.id}
                  className={`dropdown-option ${
                    assignForm.assigned_truck === truck.reg_no ? "selected" : ""
                  } ${hasActiveTrips(truck.reg_no, trips) ? "disabled" : ""}`}
                  onClick={() => {
                    if (!hasActiveTrips(truck.reg_no, trips)) {
                      setAssignForm({
                        ...assignForm,
                        assigned_truck: truck.reg_no,
                      });
                      setOpenDropdown(null);
                    }
                  }}
                >
                  {truck.reg_no} {hasActiveTrips(truck.reg_no, trips) ? "(Active Trip)" : ""}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No trucks available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Assign Trailer: {trailer.registration}</h3>
        {reassignConfirm ? (
          <>
            <p>{reassignConfirm.message}</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={performAssignment}>
                Reassign
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setReassignConfirm(null);
                  setFeedback(null);
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleAssign} className="form-block">
            {renderTruckDropdown()}
            <div className="modal-actions">
              <button type="submit" className="btn-primary">
                Save
              </button>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        )}
        {feedback && (
          <FeedbackModal
            message={feedback.message}
            type={feedback.type}
            onClose={() => setFeedback(null)}
          />
        )}
      </div>
    </div>
  );
};

export default function Trailers() {
  const [trailers, setTrailers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({
    trailer_number: "",
    type: "",
    registration: "",
    capacity: "",
    status: "Active",
    assigned_truck: "",
  });
  const [editingTrailerId, setEditingTrailerId] = useState(null);
  const [assignTrailerId, setAssignTrailerId] = useState(null);
  const [actionTrailerId, setActionTrailerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    trailerForm: true,
    trailersList: true,
  });
  const modalRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setFeedback(null);
      try {
        await Promise.all([loadTrailers(), loadTrucks(), loadTrips()]);
        console.log("Trailers, trucks, and trips loaded");
      } catch (error) {
        console.error("Error loading data:", error.code, error.message, error);
        setFeedback({
          message:
            error.code === "unavailable" || error.code === "deadline-exceeded"
              ? "Network error: Please check your connection."
              : error.code === "permission-denied"
              ? "Database error: You don't have permission to access this data."
              : `Failed to load data: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActionTrailerId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadTrailers() {
    try {
      const snaps = await getDocs(collection(db, "trailers"));
      const trailersData = snaps.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      console.log("Fetched trailers:", trailersData);
      setTrailers(trailersData);
    } catch (error) {
      console.error("Error fetching trailers:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadTrucks() {
    try {
      const snaps = await getDocs(collection(db, "trucks"));
      const trucksData = snaps.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          disposed: d.data().disposed ?? false,
        }))
        .filter((truck) => !truck.disposed);
      console.log("Fetched trucks:", trucksData);
      setTrucks(trucksData);
    } catch (error) {
      console.error("Error fetching trucks:", error.code, error.message, error);
      throw error;
    }
  }

  async function loadTrips() {
    try {
      const snaps = await getDocs(collection(db, "trips"));
      const tripsData = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log("Fetched trips:", tripsData);
      setTrips(tripsData);
    } catch (error) {
      console.error("Error fetching trips:", error.code, error.message, error);
      throw error;
    }
  }

  async function validateRegistration(registration, excludeId = null) {
    try {
      const q = query(collection(db, "trailers"), where("registration", "==", registration));
      const snaps = await getDocs(q);
      return snaps.docs.every((d) => d.id === excludeId);
    } catch (error) {
      console.error("Error validating registration:", error.code, error.message, error);
      throw error;
    }
  }

  async function createTrailer(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { trailer_number, type, registration, capacity } = form;
      if (!trailer_number || !registration) {
        setFeedback({
          message: "Please provide trailer number and registration.",
          type: "error",
        });
        return;
      }
      if (Number(capacity) < 0) {
        setFeedback({ message: "Capacity cannot be negative.", type: "error" });
        return;
      }
      const isUnique = await validateRegistration(registration);
      if (!isUnique) {
        setFeedback({ message: "Registration number must be unique.", type: "error" });
        return;
      }
      const payload = {
        trailer_number,
        type,
        registration,
        capacity: Number(capacity) || 0,
        status: form.status,
        assigned_truck: form.assigned_truck || "",
        trailer_code: "TRL-" + uuidv4().slice(0, 8),
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, "trailers"), payload);
      setForm({
        trailer_number: "",
        type: "",
        registration: "",
        capacity: "",
        status: "Active",
        assigned_truck: "",
      });
      setFeedback({ message: "Trailer added successfully.", type: "success" });
      await loadTrailers();
    } catch (error) {
      console.error("Error creating trailer:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to create trailer: ${error.message}`,
        type: "error",
      });
    }
  }

  async function updateTrailer(e) {
    e.preventDefault();
    if (!editingTrailerId) return;
    setFeedback(null);
    try {
      const { trailer_number, type, registration, capacity } = form;
      if (!trailer_number || !registration) {
        setFeedback({
          message: "Please provide trailer number and registration.",
          type: "error",
        });
        return;
      }
      if (Number(capacity) < 0) {
        setFeedback({ message: "Capacity cannot be negative.", type: "error" });
        return;
      }
      const isUnique = await validateRegistration(registration, editingTrailerId);
      if (!isUnique) {
        setFeedback({ message: "Registration number must be unique.", type: "error" });
        return;
      }
      const payload = {
        trailer_number,
        type,
        registration,
        capacity: Number(capacity) || 0,
        status: form.status,
        assigned_truck: form.assigned_truck || "",
      };
      await updateDoc(doc(db, "trailers", editingTrailerId), payload);
      setFeedback({ message: "Trailer updated successfully.", type: "success" });
      closeModal();
      await loadTrailers();
    } catch (error) {
      console.error("Error updating trailer:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to update trailer: ${error.message}`,
        type: "error",
      });
    }
  }

  async function deleteTrailer(id) {
    if (!confirm("Are you sure you want to delete this trailer?")) return;
    setFeedback(null);
    try {
      const trailer = trailers.find((t) => t.id === id);
      if (trailer.assigned_truck && hasActiveTrips(trailer.assigned_truck, trips)) {
        setFeedback({
          message: "Cannot delete this trailer because it is assigned to a truck on an active trip.",
          type: "error",
        });
        return;
      }
      await deleteDoc(doc(db, "trailers", id));
      // Update truck to remove trailer
      const truck = trucks.find((t) => t.trailers?.includes(trailer.registration));
      if (truck) {
        await updateDoc(doc(db, "trucks", truck.id), {
          trailers: truck.trailers.filter((t) => t !== trailer.registration),
        });
      }
      setFeedback({ message: "Trailer deleted successfully.", type: "success" });
      await loadTrailers();
    } catch (error) {
      console.error("Error deleting trailer:", error.code, error.message, error);
      setFeedback({
        message:
          error.code === "unavailable" || error.code === "deadline-exceeded"
            ? "Network error: Please check your connection."
            : error.code === "permission-denied"
            ? "Database error: You don't have permission to perform this action."
            : `Failed to delete trailer: ${error.message}`,
        type: "error",
      });
    }
  }

  const hasActiveTrips = (truckReg, tripsList) => {
    return tripsList.some((t) => t.truck === truckReg && t.status === "Active");
  };

  function openEditModal(trailer) {
    setForm({
      trailer_number: trailer.trailer_number,
      type: trailer.type || "",
      registration: trailer.registration,
      capacity: trailer.capacity || "",
      status: trailer.status || "Active",
      assigned_truck: trailer.assigned_truck || "",
    });
    setEditingTrailerId(trailer.id);
    setAssignTrailerId(null);
    setActionTrailerId(null);
  }

  function openAssignModal(trailer) {
    setAssignTrailerId(trailer.id);
    setEditingTrailerId(null);
    setActionTrailerId(null);
  }

  function openActionModal(trailerId) {
    setActionTrailerId(trailerId);
    setEditingTrailerId(null);
    setAssignTrailerId(null);
  }

  function closeModal() {
    setForm({
      trailer_number: "",
      type: "",
      registration: "",
      capacity: "",
      status: "Active",
      assigned_truck: "",
    });
    setEditingTrailerId(null);
    setAssignTrailerId(null);
    setActionTrailerId(null);
    setFeedback(null);
  }

  const handleSectionToggle = (sectionId) => {
    setExpandedSections({
      ...expandedSections,
      [sectionId]: !expandedSections[sectionId],
    });
  };

  return (
    <div className="trailers-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {feedback && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}
          <h2>Trailers</h2>
          <CollapsibleSection
            title="Trailer Form"
            id="trailerForm"
            isExpanded={expandedSections.trailerForm}
            onToggle={handleSectionToggle}
            externalControl={true}
          >
            <form
              onSubmit={editingTrailerId ? updateTrailer : createTrailer}
              className="form-inline"
            >
              <input
                placeholder="Trailer Number"
                value={form.trailer_number}
                onChange={(e) => setForm({ ...form, trailer_number: e.target.value })}
                required
              />
              <input
                placeholder="Type (Flatbed, Box, etc)"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              />
              <input
                placeholder="Registration"
                value={form.registration}
                onChange={(e) => setForm({ ...form, registration: e.target.value })}
                required
              />
              <input
                placeholder="Capacity (Tons)"
                type="number"
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              />
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Maintenance">Maintenance</option>
              </select>
              <button className="btn-primary" type="submit">
                {editingTrailerId ? "Save" : "Add Trailer"}
              </button>
            </form>
          </CollapsibleSection>
          <CollapsibleSection
            title="Trailers List"
            id="trailersList"
            isExpanded={expandedSections.trailersList}
            onToggle={handleSectionToggle}
            externalControl={true}
          >
            <div className="table-wrap">
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>Trailer No</th>
                    <th>Type</th>
                    <th>Registration</th>
                    <th>Capacity</th>
                    <th>Status</th>
                    <th>Assigned Truck</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {trailers.length === 0 ? (
                    <tr>
                      <td colSpan="7">No trailers found</td>
                    </tr>
                  ) : (
                    trailers.map((t) => (
                      <tr
                        key={t.id}
                        style={{
                          opacity: t.assigned_truck && hasActiveTrips(t.assigned_truck, trips) ? 0.5 : 1,
                        }}
                      >
                        <td>{t.trailer_number || "-"}</td>
                        <td>{t.type || "-"}</td>
                        <td>{t.registration || "-"}</td>
                        <td>{t.capacity || "-"}</td>
                        <td>{t.status || "Active"}</td>
                        <td>{t.assigned_truck || "-"}</td>
                        <td style={{ position: "relative" }}>
                          <button
                            className="menu-button"
                            onClick={() => openActionModal(t.id)}
                          >
                            ⋮
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
          {editingTrailerId && (
            <div className="modal-overlay">
              <div className="modal" ref={modalRef}>
                <h3>Edit Trailer: {form.registration}</h3>
                <form onSubmit={updateTrailer} className="form-inline">
                  <input
                    placeholder="Trailer Number"
                    value={form.trailer_number}
                    onChange={(e) => setForm({ ...form, trailer_number: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Type (Flatbed, Box, etc)"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  />
                  <input
                    placeholder="Registration"
                    value={form.registration}
                    onChange={(e) => setForm({ ...form, registration: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Capacity (Tons)"
                    type="number"
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                  />
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Maintenance">Maintenance</option>
                  </select>
                  <div className="modal-actions">
                    <button type="submit" className="btn-primary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          {assignTrailerId && (
            <AssignModal
              trailer={trailers.find((t) => t.id === assignTrailerId)}
              onClose={closeModal}
              onAssign={() => {
                loadTrailers();
                loadTrucks();
              }}
              trucks={trucks}
              trips={trips}
            />
          )}
          {actionTrailerId && (
            <ActionModal
              trailer={trailers.find((t) => t.id === actionTrailerId)}
              onClose={closeModal}
              onEdit={() => openEditModal(trailers.find((t) => t.id === actionTrailerId))}
              onAssign={() => openAssignModal(trailers.find((t) => t.id === actionTrailerId))}
              onDelete={() => deleteTrailer(actionTrailerId)}
              hasActiveTrips={
                trailers.find((t) => t.id === actionTrailerId).assigned_truck &&
                hasActiveTrips(
                  trailers.find((t) => t.id === actionTrailerId).assigned_truck,
                  trips
                )
              }
            />
          )}
        </>
      )}
    </div>
  );
}